/**
 * FIN-9877 / FIN-6612: Pure utility functions for filter, sort, and search
 * state merging in Run Instance Result listing.
 *
 * Extracted from the component so the logic can be unit-tested independently.
 */

const WATCHLIST_COLUMN_NAMES = [
  'watchlistCategory',
  'watchlistSubCategory',
  'watchlistKeyword',
  'externalSource',
  'watchlistSource',
  'isWhitelisted'
];

/**
 * Transform watchlist selection objects into filter criteria entries.
 * FIN-6612: Using '=' operator because the backend splits comma-separated
 * values into an IN clause via sqls.in().
 */
export const transformWatchlistFilters = (filterObj) => {
  const watchlistFilterCriteria = [];

  if (filterObj?.watchlistCategory?.length > 0) {
    watchlistFilterCriteria.push({
      columnName: 'watchlistCategory',
      logic: '=',
      value: filterObj.watchlistCategory.join(',')
    });
  }

  if (filterObj?.watchlistSubCategory?.length > 0) {
    watchlistFilterCriteria.push({
      columnName: 'watchlistSubCategory',
      logic: '=',
      value: filterObj.watchlistSubCategory.join(',')
    });
  }

  if (filterObj?.watchlistKeyword?.length > 0) {
    watchlistFilterCriteria.push({
      columnName: 'watchlistKeyword',
      logic: '=',
      value: filterObj.watchlistKeyword.join(',')
    });
  }

  if (filterObj?.externalSource?.length > 0) {
    watchlistFilterCriteria.push({
      columnName: 'externalSource',
      logic: '=',
      value: filterObj.externalSource.join(',')
    });
  }

  if (filterObj?.watchlistSource?.length > 0) {
    watchlistFilterCriteria.push({
      columnName: 'watchlistSource',
      logic: '=',
      value: filterObj.watchlistSource.join(',')
    });
  }

  if (filterObj?.isWhitelisted !== undefined) {
    watchlistFilterCriteria.push({
      columnName: 'isWhitelisted',
      logic: '=',
      value: String(filterObj.isWhitelisted)
    });
  }

  return watchlistFilterCriteria;
};

/**
 * Compute the next extraQueryParameter state given a user action (obj)
 * and the current state (extraQueryParameter).
 *
 * Handles three action types:
 *  1. Search  — obj has `search` key  → preserves existing filters + sort
 *  2. Filter  — obj has `filter` key  → replaces filters, preserves sort
 *  3. Sort    — obj has `sort` only   → preserves filters, replaces sort
 *
 * Returns the new state object to be passed to setExtraQueryParameter().
 */
export const computeNextQueryState = (obj, extraQueryParameter) => {
  const hasSearch = Array.isArray(obj?.search) && obj.search.length > 0;
  const isSearchTriggered = obj?.search !== undefined;

  if (isSearchTriggered) {
    // FIN-9652: Preserve existing watchlist + standard filters when search is triggered
    const existingWatchlistFilters = {
      watchlistCategory: extraQueryParameter.watchlistCategory,
      watchlistSubCategory: extraQueryParameter.watchlistSubCategory,
      watchlistKeyword: extraQueryParameter.watchlistKeyword,
      externalSource: extraQueryParameter.externalSource,
      watchlistSource: extraQueryParameter.watchlistSource,
      isWhitelisted: extraQueryParameter.isWhitelisted
    };

    const existingWatchlistFilterCriteria = transformWatchlistFilters(
      existingWatchlistFilters
    );

    const existingNonWatchlistFilters = (
      extraQueryParameter.filter || []
    ).filter((f) => !WATCHLIST_COLUMN_NAMES.includes(f.columnName));

    return {
      ...extraQueryParameter,
      search: hasSearch ? obj.search : [],
      filter: [...existingNonWatchlistFilters, ...existingWatchlistFilterCriteria]
    };
  }

  // FIN-9877: Distinguish between filter updates and sort-only updates.
  const isFilterTriggered = obj?.filter !== undefined;
  const watchlistFilterCriteria = transformWatchlistFilters(
    isFilterTriggered ? obj : extraQueryParameter
  );

  const baseFilters = isFilterTriggered
    ? obj.filter
    : (extraQueryParameter.filter || []).filter(
        (f) => !WATCHLIST_COLUMN_NAMES.includes(f.columnName)
      );

  const combinedFilters = [...baseFilters, ...watchlistFilterCriteria];

  return {
    ...extraQueryParameter,
    ...obj,
    filter: combinedFilters
  };
};
